import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Trophy, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function LeagueManager({ user, setUser }) {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [deletingLeague, setDeletingLeague] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    season: '',
    location: '',
    logo_url: '',
    start_date: '',
    end_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchLeagues();
  }, [search]);

  const fetchLeagues = async () => {
    try {
      const response = await axios.get(`${API}/leagues`, {
        params: search ? { search } : {}
      });
      setLeagues(response.data);
    } catch (error) {
      console.error('Error fetching leagues:', error);
      toast.error('Failed to load leagues');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (league = null) => {
    if (league) {
      setEditingLeague(league);
      setFormData({
        name: league.name,
        season: league.season,
        location: league.location,
        logo_url: league.logo_url || '',
        start_date: league.start_date,
        end_date: league.end_date
      });
    } else {
      setEditingLeague(null);
      setFormData({
        name: '',
        season: '',
        location: '',
        logo_url: '',
        start_date: '',
        end_date: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingLeague) {
        await axios.put(`${API}/leagues/${editingLeague.league_id}`, formData);
        toast.success('League updated successfully');
      } else {
        await axios.post(`${API}/leagues`, formData);
        toast.success('League created successfully');
      }
      setDialogOpen(false);
      fetchLeagues();
    } catch (error) {
      console.error('Error saving league:', error);
      toast.error(error.response?.data?.detail || 'Failed to save league');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingLeague) return;

    try {
      await axios.delete(`${API}/leagues/${deletingLeague.league_id}`);
      toast.success('League deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingLeague(null);
      fetchLeagues();
    } catch (error) {
      console.error('Error deleting league:', error);
      toast.error('Failed to delete league');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Leagues</h1>
              <p className="text-slate-500 mt-1">Manage cricket leagues</p>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              data-testid="create-league-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create League
            </Button>
          </div>

          {/* Search */}
          <div className="mb-6 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search leagues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 rounded-xl"
              data-testid="league-search"
            />
          </div>

          {/* Leagues Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton h-48 rounded-2xl"></div>
              ))}
            </div>
          ) : leagues.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {leagues.map((league) => (
                <Card key={league.league_id} className="glass-card rounded-2xl overflow-hidden" data-testid={`league-${league.league_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      {league.logo_url ? (
                        <img src={league.logo_url} alt={league.name} className="w-14 h-14 rounded-xl object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl team-logo-placeholder text-xl">
                          {league.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg text-slate-900 truncate">{league.name}</h3>
                        <Badge variant="secondary" className="mt-1">{league.season}</Badge>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {league.location}
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {formatDate(league.start_date)} - {formatDate(league.end_date)}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-lg"
                        onClick={() => handleOpenDialog(league)}
                        data-testid={`edit-league-${league.league_id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        onClick={() => {
                          setDeletingLeague(league);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-league-${league.league_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <Trophy className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No leagues found</h3>
              <p className="text-slate-500 mt-2">Create your first league to get started</p>
              <Button 
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create League
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLeague ? 'Edit League' : 'Create New League'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">League Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., NYC Cricket League"
                  required
                  data-testid="league-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="season">Season *</Label>
                <Input
                  id="season"
                  value={formData.season}
                  onChange={(e) => setFormData(prev => ({ ...prev, season: e.target.value }))}
                  placeholder="e.g., Spring 2026"
                  required
                  data-testid="league-season-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., New York, NY"
                  required
                  data-testid="league-location-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    required
                    data-testid="league-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    required
                    data-testid="league-end-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo">League Logo</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
                {formData.logo_url && (
                  <div className="mt-2">
                    <img src={formData.logo_url} alt="Preview" className="w-16 h-16 rounded-xl object-cover" />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800"
                disabled={submitting}
                data-testid="save-league-btn"
              >
                {submitting ? 'Saving...' : (editingLeague ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete League?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingLeague?.name}" and all associated teams, players, and matches. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-league"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
